import type { Consent as ConsentModel, Entity, UserDetailConsent } from 'models';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ButtonProperties, Form, Space, Title } from 'ui';

import type { Consent, ConsentOption } from '../../types';
import { ConsentDetail } from './consent-detail';
import { ConsentsFormItem } from './consent-form-item';
import { ConsentsDescription } from './consents-description';
import { ConsentsFormActions } from './consents-form-actions';

type FormValues = {
  consents: Consent[];
};

type P = {
  acceptTitle?: string;
  consents: ConsentModel[];
  currentAcceptedConsents: UserDetailConsent[];
  entities: Entity[];
  infoMessage?: string;
  left?: ButtonProperties;
  onAccept: () => Promise<void> | void;
  onConsent?: (id?: string) => void;
  onReject?: () => Promise<void> | void;
  rejectTitle?: string;
  showInfoMessage?: boolean;
  showTitle?: boolean;
  submit?: ButtonProperties;
};

const generateInitialValues = ({
  consents,
  currentAcceptedConsents,
}: {
  consents: ConsentModel[];
  currentAcceptedConsents: UserDetailConsent[];
}): Partial<FormValues> => ({
  consents: consents.map((consent) => ({
    consented:
      currentAcceptedConsents.find((currentAcceptedConsent) => currentAcceptedConsent.id === consent.id)?.consented ??
      false,
    htmlContent: consent.htmlContent,
    id: consent.id,
    name: consent.name,
  })),
});

const ConsentsForm = ({
  acceptTitle,
  consents,
  currentAcceptedConsents,
  entities,
  infoMessage,
  left,
  onAccept,
  onConsent,
  onReject,
  rejectTitle,
  showInfoMessage = true,
  showTitle = true,
  submit,
}: P) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [selectedConsent, setSelectedConsent] = useState<ConsentOption>();
  const [isAccepting, setIsAccepting] = useState<boolean>();
  const [isRejecting, setIsRejecting] = useState<boolean>();

  const initialValues = generateInitialValues({ consents, currentAcceptedConsents });

  const onSelectConsent = (consent?: ConsentOption) => {
    onConsent?.(consent?.value.id);
    setSelectedConsent(consent);
  };

  const onFinish = async () => {
    setIsAccepting(true);
    await onAccept();
    setIsAccepting(false);
  };

  const onClose = async () => {
    setIsRejecting(true);
    await onReject?.();
    setIsRejecting(false);
  };

  return (
    <Form<FormValues> className="consents-form" form={form} initialValues={initialValues} onFinish={onFinish}>
      {selectedConsent ? (
        <ConsentDetail
          consent={selectedConsent.value}
          onAccept={() => {
            selectedConsent.onChange({ ...selectedConsent.value, consented: true });
            onSelectConsent();
          }}
          onCancel={() => {
            onSelectConsent();
          }}
        />
      ) : (
        <>
          {showTitle ? (
            <Title level={2} marginBottom>
              Confirm consents
            </Title>
          ) : null}

          <ConsentsDescription entities={entities} infoMessage={infoMessage} showInfoMessage={showInfoMessage} />

          <Form.List name="consents">
            {(fields) => (
              <Space block orientation="vertical" size="xxs">
                {fields.map((field) => (
                  <Form.Item<number>
                    {...field}
                    key={`consents-${field.key}`}
                    rules={[
                      {
                        // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                        async validator(_, value: Consent) {
                          if (!value.consented) {
                            throw new Error(t('Error: Consent for {{label}} is required', { label: value.name }));
                          }
                        },
                      },
                    ]}
                  >
                    <ConsentsFormItem
                      onSelect={(data) => {
                        onSelectConsent(data);
                      }}
                    />
                  </Form.Item>
                ))}
              </Space>
            )}
          </Form.List>

          <ConsentsFormActions
            acceptTitle={acceptTitle}
            isAccepting={isAccepting}
            isRejecting={isRejecting}
            left={left}
            onClose={onClose}
            onReject={onReject}
            rejectTitle={rejectTitle}
            submit={submit}
          />
        </>
      )}
    </Form>
  );
};

export default ConsentsForm;
